import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Lock, FileText, Target, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getDeckScore, analyzeDeck } from "@/lib/deck.functions";
import { createCheckout } from "@/lib/payments.functions";
import { getActiveProgramCount } from "@/lib/public.functions";
import { posthog } from "@/lib/posthog";
import { Sentry } from "@/lib/sentry";
import { usePricing } from "@/lib/pricing";

type ApplySearch = { deck?: string; resume?: boolean };

export const Route = createFileRoute("/apply")({
  validateSearch: (search: Record<string, unknown>): ApplySearch => {
    const rawResume = search.resume;
    const resume = rawResume === true || rawResume === "1" || rawResume === "true";
    return {
      deck: typeof search.deck === "string" ? search.deck : undefined,
      resume: resume ? true : undefined,
    };
  },
  head: () => ({
    meta: [
      { title: "A transaction advisor submits your deck to 10+ programs - DeckScore" },
      {
        name: "description",
        content:
          "A human transaction advisor submits your full deck and application details into 10+ matched accelerators, incubators, VCs and fitting grant programmes within 48 hours. Flat one-time fee, refunded if we miss it.",
      },
      { property: "og:title", content: "A transaction advisor submits your deck to 10+ programs - DeckScore" },
      {
        property: "og:description",
        content:
          "A human transaction advisor submits your full deck and application details into 10+ matched accelerators, incubators, VCs and fitting grant programmes within 48 hours. Flat one-time fee, refunded if we miss it.",
      },

      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://deckscore.ai/apply" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://deckscore.ai/apply" }],
  }),
  component: ApplyPage,
});

function Field({
  label,
  optional,
  children,
}: {
  label: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[13px] font-medium text-ink">
        {label}
        {optional && <span className="ml-1.5 text-ink-tertiary font-normal">(optional)</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "min-h-[44px] rounded-xl border border-line bg-bg px-3.5 text-[14.5px] text-ink placeholder:text-ink-tertiary focus:border-accent focus-visible:outline-none";


function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}
function getAnonToken(): string {
  try {
    const k = "deckscore_anon_token";
    let t = localStorage.getItem(k);
    if (!t) { t = crypto.randomUUID(); localStorage.setItem(k, t); }
    return t;
  } catch { return crypto.randomUUID(); }
}

const DRAFT_KEY = "deckscore_apply_draft";

type ApplyDraft = {
  company: string;
  elevatorPitch: string;
  stage: string;
  raising: string;
  valuation: string;
  vcBacked: boolean | null;
  mrr: string;
  revenueModel: string;
  location: string;
  founderEmail: string;
  consent: boolean;
  deckId: string | null;
};

function saveDraft(obj: ApplyDraft) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(obj)); } catch { /* ignore */ }
}

function loadDraft(): ApplyDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ApplyDraft;
  } catch { return null; }
}

function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
}

function OfferHeader() {
  const pricing = usePricing();
  return (
    <>
      <p className="font-script text-accent text-[22px] leading-none">investor submission</p>
      <h1 className="font-display text-[32px] md:text-[44px] font-semibold mt-2 leading-[1.05] tracking-tight">
        One advisor. 10+ applications. 48 hours.
      </h1>
      <p className="mt-3 text-[15px] md:text-[16px] text-ink-secondary leading-snug">
        A transaction advisor enters your deck and details into the application systems of matched accelerators, incubators &amp; VCs - plus fitting grant programmes.
      </p>
      <div className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="font-display text-[28px] md:text-[32px] font-bold text-ink">{pricing.submissionAmountDisplay}</span>
        {pricing.submissionApprox && (
          <span className="text-[13px] text-ink-tertiary">{pricing.submissionApprox}</span>
        )}
        <span className="text-[15px] text-ink-secondary">· one-time</span>
        <span className="text-[14px] text-ink-tertiary">·</span>
        <span className="text-[14px] md:text-[15px] font-medium text-ink">
          Submitted within 48 hours or your money back.
        </span>
      </div>
    </>
  );
}

function OfferSupport({ programCount }: { programCount: number | null }) {
  return (
    <>
      <p className="text-[14.5px] text-ink-secondary leading-relaxed">
        Each program's application form takes 20-40 minutes to fill properly. We do all of them for you - deck, details, everything.
      </p>
      {programCount != null && programCount > 0 && (
        <p className="mt-3 text-[13px] text-ink-tertiary">
          Currently matching against <span className="font-semibold text-ink">{programCount}</span> active programs.
        </p>
      )}
    </>
  );
}


function WhatYouGet() {
  const items: { icon: ReactNode; text: string }[] = [
    { icon: <Target className="h-4 w-4" />, text: "Entered into each program's own system - not a mass email" },
    { icon: <FileText className="h-4 w-4" />, text: "Fitting grant programmes included" },
    { icon: <FileText className="h-4 w-4" />, text: "Bonus one-pager - yours to keep" },
    { icon: <Target className="h-4 w-4" />, text: "Marketplace listing where relevant" },
  ];
  return (
    <section aria-label="What you get" className="mb-6 grid gap-3 sm:grid-cols-2">
      {items.map((it, i) => (
        <div key={i} className="rounded-2xl border border-line bg-surface p-4 flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent/15 text-accent shrink-0">
            {it.icon}
          </span>
          <span className="text-[13.5px] text-ink leading-snug">{it.text}</span>
        </div>
      ))}
    </section>
  );
}



function SampleOnePager() {
  return (
    <section aria-label="Sample one-pager" className="mb-6">
      <div className="relative overflow-hidden rounded-2xl border border-line bg-surface p-5">
        <div className="relative rounded-xl bg-white text-neutral-800 shadow-inner border border-line p-5 select-none">
          <div className="flex items-center justify-between border-b border-neutral-200 pb-2 mb-3">
            <div>
              <div className="text-[15px] font-bold tracking-tight">NimbusAI</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">seed · $500k raise</div>
            </div>
            <div className="text-[10px] text-neutral-500">one-pager</div>
          </div>
          <p className="text-[10.5px] leading-snug text-neutral-700">
            NimbusAI turns unstructured support tickets into resolved cases automatically for mid-market SaaS teams.
          </p>
          <div className="mt-3 grid grid-cols-3 gap-2 text-[9.5px]">
            <div className="rounded bg-neutral-100 p-2">
              <div className="font-semibold text-neutral-800">Traction</div>
              <div className="text-neutral-600">$18k MRR · 32% MoM</div>
            </div>
            <div className="rounded bg-neutral-100 p-2">
              <div className="font-semibold text-neutral-800">Market</div>
              <div className="text-neutral-600">$14B SAM</div>
            </div>
            <div className="rounded bg-neutral-100 p-2">
              <div className="font-semibold text-neutral-800">Team</div>
              <div className="text-neutral-600">ex-Zendesk, ex-Stripe</div>
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            <div className="h-1.5 rounded bg-neutral-100" />
            <div className="h-1.5 w-11/12 rounded bg-neutral-100" />
            <div className="h-1.5 w-9/12 rounded bg-neutral-100" />
            <div className="h-1.5 w-10/12 rounded bg-neutral-100" />
          </div>
          <div className="pointer-events-none absolute inset-0 backdrop-blur-[1.5px] bg-white/10" />
        </div>
        <div className="pointer-events-none absolute top-3 right-3 rounded-full bg-ink/80 text-bg text-[10px] uppercase tracking-widest px-2.5 py-1">
          Sample one-pager
        </div>
      </div>
    </section>
  );
}

function AfterPayTimeline() {
  const pricing = usePricing();
  const steps = [
    { n: 1, title: `Pay ${pricing.submissionAmountDisplay}`, body: "Secure checkout via Razorpay." },
    { n: 2, title: "Advisor assigned, one-pager generated", body: "" },
    { n: 3, title: "Submitted to each matched program - within 48h", body: "" },
    { n: 4, title: "Track it from your dashboard", body: "" },
  ];


  return (
    <section aria-label="What happens after you pay" className="mt-6 rounded-2xl border border-line bg-surface p-6 md:p-7">
      <h2 className="font-display text-[18px] font-semibold text-ink">What happens after you pay</h2>
      <ol className="mt-4 grid gap-3 sm:grid-cols-2">
        {steps.map((s) => (
          <li key={s.n} className="flex gap-3 rounded-xl border border-line bg-surface-sunken p-3.5">
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-accent-ink text-[13px] font-bold">
              {s.n}
            </span>
            <div>
              <div className="text-[14px] font-semibold text-ink leading-tight">{s.title}</div>
              {s.body && <div className="text-[13px] text-ink-secondary mt-0.5 leading-snug">{s.body}</div>}
            </div>

          </li>
        ))}
      </ol>
    </section>
  );
}

function FaqItem({ q, children }: { q: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-line">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 py-4 text-left"
        aria-expanded={open}
      >
        <span className="text-[14.5px] font-semibold text-ink">{q}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-ink-tertiary transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-4 text-[13.5px] text-ink-secondary leading-relaxed">{children}</div>}
    </div>
  );
}

function MiniFaq() {
  const pricing = usePricing();
  const priceLabel = pricing.submissionAmountDisplay;
  return (
    <section aria-label="FAQ" className="mt-6 rounded-2xl border border-line bg-surface p-6 md:p-7">
      <h2 className="font-display text-[18px] font-semibold text-ink mb-2">Common questions</h2>
      <FaqItem q="Who sees my deck?">
        Only the matched programs we submit to. Your deck is never listed publicly and never sold or shared with anyone else.
      </FaqItem>
      <FaqItem q="What if nobody replies?">
        We guarantee submission to matched programs within 48 hours - if we miss that, you're refunded. We can't guarantee replies, meetings or investment; no one honestly can. What we control is that your deck and application land inside each program's own system, quickly.
      </FaqItem>
      <FaqItem q={`Why only ${priceLabel}?`}>
        Because the free deck score qualifies applicants, and we'd rather charge a flat {priceLabel} for the advisor's submission work than take 2-5% of your round like a broker.
      </FaqItem>

    </section>
  );
}

function ApplyPage() {
  const { deck: deckId, resume } = Route.useSearch();
  const navigate = useNavigate();
  const fetchScore = useServerFn(getDeckScore);
  const runCreateCheckout = useServerFn(createCheckout);
  const runAnalyze = useServerFn(analyzeDeck);
  const fetchProgramCount = useServerFn(getActiveProgramCount);
  const pricing = usePricing();

  const [authChecked, setAuthChecked] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessionEmail, setSessionEmail] = useState<string>("");

  const [company, setCompany] = useState("");
  const [elevatorPitch, setElevatorPitch] = useState("");
  const [stage, setStage] = useState("");
  const [raising, setRaising] = useState("");
  const [valuation, setValuation] = useState("");
  const [vcBacked, setVcBacked] = useState<boolean | null>(null);
  const [mrr, setMrr] = useState("");
  const [revenueModel, setRevenueModel] = useState("");
  const [location, setLocation] = useState("");
  const [founderEmail, setFounderEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deckFile, setDeckFile] = useState<File | null>(null);
  const [uploadingDeck, setUploadingDeck] = useState(false);
  const [deckError, setDeckError] = useState<string | null>(null);

  const { data: programCountData } = useQuery({
    queryKey: ["active-program-count"],
    queryFn: () => fetchProgramCount({}),
    staleTime: 5 * 60_000,
  });
  const programCount = programCountData?.count ?? null;

  async function onUploadDeck() {
    if (!deckFile || uploadingDeck) return;
    setUploadingDeck(true);
    setDeckError(null);
    try {
      const fileBase64 = await fileToBase64(deckFile);
      const { getStoredRef } = await import("@/lib/ref");
      const { deckId: newDeck } = await runAnalyze({
        data: {
          fileBase64,
          fileName: deckFile.name,
          mimeType: deckFile.type || "application/pdf",
          anonToken: getAnonToken(),
          ref: getStoredRef() ?? undefined,
          forSubmission: true,
        },
      });
      navigate({ to: "/apply", search: { deck: newDeck } });

    } catch (err) {
      console.error(err);
      const raw = err instanceof Error ? err.message : "Couldn't read that deck. Try again.";
      const msg = /FREE_LIMIT/i.test(raw)
        ? `You've used all 5 free analyses. Reaching investors (${pricing.submissionAmountDisplay}) has no limit.`
        : /RATE_LIMITED/i.test(raw)
        ? "You've hit today's analysis limit. Please try again tomorrow."
        : raw;
      setDeckError(msg);
      setUploadingDeck(false);
    }
  }

  const resumedRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const session = data.session;
      if (session) {
        setIsLoggedIn(true);
        setSessionEmail(session.user.email ?? "");
        setFounderEmail((prev) => prev || session.user.email || "");
      }
      setAuthChecked(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      if (session) {
        setIsLoggedIn(true);
        setSessionEmail(session.user.email ?? "");
        setFounderEmail((prev) => prev || session.user.email || "");
      } else {
        setIsLoggedIn(false);
        setSessionEmail("");
      }
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const { data: scoreRow } = useQuery({
    queryKey: ["deck-score", deckId],
    queryFn: () => fetchScore({ data: { deckId: deckId! } }),
    enabled: !!deckId && authChecked,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!scoreRow) return;
    const meta = (scoreRow as { analysis?: { deck_meta?: Record<string, unknown> } })?.analysis
      ?.deck_meta;
    if (!meta) return;
    setCompany((prev) => prev || String(meta.company_name ?? ""));
    setElevatorPitch((prev) => prev || String(meta.one_liner ?? ""));
    setStage((prev) => prev || String(meta.stage_detected ?? ""));
  }, [scoreRow]);

  const overallScore = (scoreRow as { overall_score?: number } | undefined)?.overall_score;
  const showScoreNudge = typeof overallScore === "number" && overallScore >= 60;

  // Auto-resume after magic-link return
  useEffect(() => {
    if (!authChecked || !isLoggedIn || !resume) return;
    if (resumedRef.current) return;
    resumedRef.current = true;
    const draft = loadDraft();
    if (!draft) return;

    setCompany(draft.company ?? "");
    setElevatorPitch(draft.elevatorPitch ?? "");
    setStage(draft.stage ?? "");
    setRaising(draft.raising ?? "");
    setValuation(draft.valuation ?? "");
    setVcBacked(draft.vcBacked ?? null);
    setMrr(draft.mrr ?? "");
    setRevenueModel(draft.revenueModel ?? "");
    setLocation(draft.location ?? "");
    setFounderEmail(draft.founderEmail ?? "");
    setConsent(!!draft.consent);

    if (draft.consent !== true) return;

    (async () => {
      setSubmitting(true);
      setError(null);
      try {
        try { Sentry.setTag("deck_id", (draft.deckId ?? deckId) ?? "none"); } catch { /* non-fatal */ }
        const { url, submissionId } = await runCreateCheckout({
          data: {
            origin: window.location.origin,
            consent: true,
            form: {
              deckId: draft.deckId ?? deckId ?? null,
              company: draft.company,
              elevatorPitch: draft.elevatorPitch,
              stage: draft.stage,
              raising: draft.raising,
              valuation: draft.valuation || undefined,
              vcBacked: draft.vcBacked ?? undefined,
              mrr: draft.mrr || undefined,
              revenueModel: draft.revenueModel || undefined,
              location: draft.location,
              founderEmail: draft.founderEmail,
            },
          },
        });
        if (!url) throw new Error("The payment gateway didn't return a checkout URL.");
        try { posthog.capture("checkout_started", { submission_id: submissionId }); } catch { /* non-fatal */ }
        clearDraft();
        window.location.href = url;
      } catch (err: unknown) {
        console.error(err);
        const msg =
          err instanceof Error && err.message
            ? err.message
            : "Couldn't resume checkout. Please try again.";
        setError(msg);
        setSubmitting(false);
      }
    })();
  }, [authChecked, isLoggedIn, resume, deckId, runCreateCheckout]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!consent || submitting) return;
    setError(null);

    if (!isLoggedIn) {
      saveDraft({
        company, elevatorPitch, stage, raising, valuation, vcBacked, mrr, revenueModel, location, founderEmail,
        consent: true,
        deckId: deckId ?? null,
      });
      const next = deckId ? `/apply?deck=${deckId}&resume=1` : `/apply?resume=1`;
      navigate({ to: "/login", search: { next, deck: deckId } });
      return;
    }

    setSubmitting(true);
    try {
      try { Sentry.setTag("deck_id", deckId ?? "none"); } catch { /* non-fatal */ }
      const { url, submissionId } = await runCreateCheckout({
        data: {
          origin: window.location.origin,
          consent: true,
          form: {
            deckId: deckId ?? null,
            company,
            elevatorPitch,
            stage,
            raising,
            valuation: valuation || undefined,
            vcBacked: vcBacked ?? undefined,
            mrr: mrr || undefined,
            revenueModel: revenueModel || undefined,
            location,
            founderEmail,
          },
        },
      });
      if (!url) throw new Error("The payment gateway didn't return a checkout URL.");
      try { posthog.capture("checkout_started", { submission_id: submissionId }); } catch { /* non-fatal */ }
      clearDraft();
      window.location.href = url;
    } catch (err: unknown) {
      console.error(err);
      const msg = err instanceof Error && err.message ? err.message : "Couldn't start checkout. Please try again.";
      setError(msg);
      setSubmitting(false);
    }
  }

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-bg text-ink grid place-items-center">
        <p className="text-ink-tertiary text-[13px]">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg text-ink flex flex-col">
      <header className="sticky top-0 z-30 border-b border-line bg-bg">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-block h-[18px] w-[18px] rounded-md bg-accent" />
            <span className="font-display text-[15px] font-semibold">DeckScore</span>
          </Link>
          <div className="flex items-center gap-4">
            {isLoggedIn && (
              <Link to="/dashboard" className="text-[13px] text-ink-secondary hover:text-ink">
                Dashboard
              </Link>
            )}
            <span className="text-[12.5px] text-ink-tertiary">
              {isLoggedIn ? sessionEmail : "Not signed in"}
            </span>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className={`${deckId ? "max-w-[720px]" : "max-w-[1180px]"} mx-auto px-5 md:px-10 py-8 md:py-14`}>
          {!deckId ? (
            <>
              {(() => {
                const uploadCard = (
                  <div className="bg-surface border-2 border-accent/40 rounded-2xl p-6 md:p-7 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.35)] md:sticky md:top-20">
                    <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-accent/15 px-3 py-1 text-[11.5px] font-bold uppercase tracking-widest text-accent">
                      Start here - drop your deck
                    </div>
                    <h2 className="font-display text-[18px] font-semibold">Upload your pitch deck</h2>
                    <p className="text-[13.5px] text-ink-secondary mt-1">
                      We'll read it, attach it to your submission, and prefill your details. This doesn't use up your free analyses.
                    </p>
                    <label
                      htmlFor="apply-deck"
                      className="mt-4 flex min-h-[180px] cursor-pointer flex-col items-center justify-center rounded-2xl border-[1.5px] border-dashed border-line bg-surface-sunken p-6 text-center hover:border-accent transition"
                    >
                      <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 text-xl text-accent">⬆</div>
                      <div className="text-[15px] font-semibold text-ink">{deckFile ? deckFile.name : "Drop your deck (PDF)"}</div>
                      <div className="mt-1 text-[13px] text-ink-tertiary">{deckFile ? "Ready" : "or click to choose a file"}</div>
                      <input id="apply-deck" type="file" accept="application/pdf" className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) setDeckFile(f); }} disabled={uploadingDeck} />
                    </label>
                    {deckError && <p className="mt-2 text-[13px] text-[#C65C4E]">{deckError}</p>}
                    <button type="button" onClick={onUploadDeck} disabled={!deckFile || uploadingDeck}
                      className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center rounded-full bg-accent px-6 text-[15px] font-bold text-accent-ink transition hover:bg-accent-press disabled:opacity-60">
                      {uploadingDeck ? "Analyzing your deck…" : "Continue"}
                    </button>
                    <p className="mt-3 text-center text-[12.5px] text-ink-tertiary">
                      Already analyzed a deck? <Link to="/dashboard" className="underline text-accent">Pick it from your dashboard</Link>.
                    </p>
                  </div>
                );
                return (
                  <>
                    <section className="grid gap-8 md:gap-10 md:grid-cols-[minmax(0,1fr)_minmax(340px,440px)] md:items-start">
                      <div className="flex flex-col gap-6">
                        <OfferHeader />
                        <div className="hidden md:block">
                          <OfferSupport programCount={programCount} />
                        </div>
                      </div>
                      <div>{uploadCard}</div>
                      <div className="md:hidden">
                        <OfferSupport programCount={programCount} />
                      </div>
                    </section>

                    <div className="mt-10 md:mt-14 max-w-[720px] mx-auto">
                      <WhatYouGet />
                      <SampleOnePager />
                      <AfterPayTimeline />
                      <MiniFaq />
                    </div>
                  </>
                );
              })()}
            </>

          ) : (
            <>
              <p className="font-script text-accent text-[22px] leading-none">investor submission</p>
              <h1 className="font-display text-[26px] md:text-[32px] font-semibold mt-2 mb-4 leading-[1.1] tracking-tight">
                Finish your submission
              </h1>
              <div className="mb-4 rounded-xl border border-line bg-surface px-4 py-3 text-[13px] md:text-[13.5px] text-ink">
                <span className="font-semibold">{pricing.submissionAmountDisplay}</span>
                {pricing.submissionApprox && (
                  <span className="text-ink-tertiary"> {pricing.submissionApprox}</span>
                )}
                <span className="text-ink-tertiary"> · </span>
                <span>your advisor submits within 48h</span>
                <span className="text-ink-tertiary"> · </span>
                <span>refunded if late</span>
              </div>



              {showScoreNudge && (
                <div className="mb-4 rounded-xl border border-accent/40 bg-accent/10 px-4 py-3 text-[13.5px] text-ink">
                  Your deck scored <span className="font-semibold">{overallScore}/100</span> - a strong base for this submission.
                </div>
              )}

              <form onSubmit={onSubmit} className="bg-surface border border-line rounded-2xl p-6 md:p-8 flex flex-col gap-5">
                <div className="flex items-center gap-2 rounded-xl border border-line bg-surface-sunken px-3.5 py-2.5 text-[13px] text-ink-secondary">
                  <Lock className="h-3.5 w-3.5 text-ink-tertiary" />
                  <span>Deck attached{company ? ` - ${company}` : ""} · locked to this submission</span>
                </div>
                <Field label="Company">
                  <input required className={inputCls} value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Inc." />
                </Field>
                <Field label="Elevator pitch">
                  <textarea
                    required
                    className={`${inputCls} min-h-[96px] py-2.5 resize-y`}
                    value={elevatorPitch}
                    onChange={(e) => setElevatorPitch(e.target.value)}
                    placeholder="What you do, for whom, and why it matters - in 1-2 sentences."
                  />
                </Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Fundraising stage">
                    <input required className={inputCls} value={stage} onChange={(e) => setStage(e.target.value)} placeholder="Pre-seed / Seed / Series A" />
                  </Field>
                  <Field label="How much are you currently raising?">
                    <input required className={inputCls} value={raising} onChange={(e) => setRaising(e.target.value)} placeholder="$500k" />
                  </Field>
                  <Field label="Current or target valuation" optional>
                    <input className={inputCls} value={valuation} onChange={(e) => setValuation(e.target.value)} placeholder="$5M post" />
                  </Field>
                  <Field label="Are you VC-backed?" optional>
                    <div className="flex gap-2">
                      {([
                        ["Yes", true],
                        ["No", false],
                      ] as const).map(([label, val]) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() => setVcBacked(vcBacked === val ? null : val)}
                          className={`min-h-[44px] flex-1 rounded-full border px-4 text-[14px] transition ${
                            vcBacked === val
                              ? "border-accent bg-accent/15 text-ink"
                              : "border-line text-ink-secondary hover:border-ink"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Monthly recurring revenue (MRR)" optional>
                    <input className={inputCls} value={mrr} onChange={(e) => setMrr(e.target.value)} placeholder="$4,200" />
                  </Field>
                  <Field label="Revenue model" optional>
                    <input className={inputCls} value={revenueModel} onChange={(e) => setRevenueModel(e.target.value)} placeholder="SaaS, transactional, ads…" />
                  </Field>
                  <Field label="Location">
                    <input required className={inputCls} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, Country" />
                  </Field>
                  <Field label="Founder email">
                    <input required type="email" className={inputCls} value={founderEmail} onChange={(e) => setFounderEmail(e.target.value)} />
                  </Field>
                </div>

                <label className="flex items-start gap-3 rounded-xl bg-surface-sunken border border-line p-3.5 mt-2">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[var(--color-accent)]"
                  />
                  <span className="text-[13.5px] text-ink-secondary">
                    I authorize DeckScore to submit this application on my behalf using the details above.
                  </span>
                </label>

                {error && <p className="text-[13px] text-[#C65C4E]">{error}</p>}

                <button
                  type="submit"
                  disabled={!consent || submitting}
                  className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-accent px-6 text-[16px] font-bold text-accent-ink transition hover:-translate-y-px hover:bg-accent-press disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {submitting ? "Opening secure checkout…" : `Pay ${pricing.submissionAmountDisplay} - submit my application`}
                </button>
                <p className="text-center text-[12.5px] text-ink-tertiary -mt-1">
                  Secure checkout via Razorpay · one-time · refunded if not submitted within 48h
                </p>
                {pricing.currency !== "INR" && (
                  <p className="text-center text-[12px] text-ink-tertiary -mt-2">
                    Billed in INR (about {pricing.submissionAmountDisplay})
                  </p>
                )}
              </form>

              <div className="mt-6">
                <WhatYouGet />
              </div>
              <AfterPayTimeline />
              <MiniFaq />
            </>
          )}

          <div className="max-w-[720px] mx-auto">
            <section className="mt-6 rounded-2xl border border-line bg-sky/60 p-6 md:p-7">
              <h2 className="font-display text-[18px] font-semibold text-ink">
                How your application reaches programs
              </h2>
              <ul className="mt-3 space-y-2 text-[14px] text-ink leading-relaxed">
                <li>· Matched programs only - never a blast list.</li>
                <li>· Entered into each program's own system within 48h of payment.</li>
                <li>· Refunded if we miss it.</li>
              </ul>
              <p className="mt-3 text-[12.5px] text-ink-tertiary">
                This is a best-effort submission service. It does not guarantee a reply, meeting,
                or investment.
              </p>
            </section>
          </div>

        </div>
      </main>
    </div>
  );
}

